"use client";

import * as React from "react";
import { motion, type Variants } from "motion/react";
import { cn } from "@/lib/utils";

interface SectionProps {
  id?: string;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
  background?: "default" | "muted" | "gradient" | "dark" | "hero";
  pattern?: boolean;
  containerSize?: "default" | "narrow" | "wide";
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0, 0, 0.2, 1] } },
};

export function Section({
  id,
  ariaLabel,
  className,
  children,
  background = "default",
  pattern = false,
  containerSize = "default",
}: SectionProps) {
  return (
    <section
      id={id}
      aria-label={ariaLabel}
      className={cn(
        "section-padding relative overflow-hidden",
        background === "default" && "bg-background",
        background === "muted" && "bg-muted/50 dark:bg-muted/30",
        background === "gradient" && "bg-gradient-hero",
        background === "dark" && "bg-invert text-invert-foreground",
        background === "hero" && "bg-gradient-hero",
        className,
      )}
    >
      {pattern && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.03] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:32px_32px] pointer-events-none"
          />
          <div
            aria-hidden
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-red/40 to-transparent"
          />
        </>
      )}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className={cn(
          "relative",
          containerSize === "narrow" && "max-w-5xl",
          containerSize === "wide" && "max-w-[1720px]",
          containerSize === "default" && "container-1720",
          containerSize !== "narrow" && "mx-auto",
        )}
      >
        <motion.div variants={itemVariants}>{children}</motion.div>
      </motion.div>
    </section>
  );
}

export const sectionItemVariants = itemVariants;
